import React, { useEffect, useReducer, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Row, Col} from 'react-bootstrap';
import moment from 'moment';
import 'moment/locale/es';
import MessageBox from '../../components/MessageBox';
import { EntityList, PageHead } from '../../components/EntityList';
import { getUserInfo } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import AccountService from '../../services/account.service';
import ContractService from '../../services/contract.service';
import AssetSaleService from '../../services/assetSale.service';
import AssetService from '../../services/asset.service';
import PaymentService from '../../services/payment.service';
import GeneratedDebtService from '../../services/generatedDebts.service';
import DebtService from '../../services/debt.service';
import CreditService from '../../services/credit.service';
import DebtConceptService from '../../services/debtConcept.service';
import CalendarView from '../../components/Calendar/CalendarView';
import settingsService from '../../services/settings.service';
import { getError } from '../../utils';
import KpiGrid from './modules/KpiGrid';
import IncomePaymentsPanel from './modules/IncomePaymentsPanel';
import UpcomingDues from './modules/UpcomingDues';
import ActivityFeed from './modules/ActivityFeed';
import QuickActionsRow from './modules/QuickActionsRow';
import { isFacturacionEnabled } from '../../utils/featureFlags';
import './Dashboard.css';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return {
			...state,
			accounts: action.payload.accounts || [],
			contracts: action.payload.contracts || [],
			assetSales: action.payload.assetSales || [],
			assets: action.payload.assets || [],
			payments: action.payload.payments || [],
			generatedDebts: action.payload.generatedDebts || [],
			debts: action.payload.debts || [],
			credits: action.payload.credits || [],
			previousMonthPayments: action.payload.previousMonthPayments || [],
			upcomingDues: action.payload.upcomingDues || [],
			loading: false,
			loaded: true,
		};
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	default:
		return state;
	}
};

function Dashboard() {
	moment.locale('es');
	const { t } = useLabels();

	const [dashboardModules, setDashboardModules] = useState(['assets', 'contracts', 'debts', 'credits', 'accounts', 'facturacion', 'income', 'calendar']);

	const [{ loading, loaded, error, accounts, contracts, assetSales, assets, payments, generatedDebts, debts, credits, previousMonthPayments, upcomingDues }, dispatch] = useReducer(reducer, {
		loading: true,
		loaded: false,
		error: '',
		accounts: [],
		contracts: [],
		assetSales: [],
		assets: [],
		payments: [],
		generatedDebts: [],
		debts: [],
		credits: [],
		previousMonthPayments: [],
		upcomingDues: [],
	});

	const [debtConcepts, setDebtConcepts] = useState([]);

	// Fetch dashboard modules configuration
	useEffect(() => {
		let isMounted = true;
		const defaultModules = ['assets', 'contracts', 'debts', 'credits', 'accounts', 'facturacion', 'income', 'calendar'];
		
		const fetchDashboardModules = async () => {
			try {
				const { data } = await settingsService.getDashboardModules();
				if (isMounted) {
					if (Array.isArray(data) && data.length > 0) {
						setDashboardModules(data);
					} else {
						// If data is not a valid array, use default
						setDashboardModules(defaultModules);
					}
				}
			} catch (err) {
				console.error('Error fetching dashboard modules:', err);
				// Use default if error
				if (isMounted) {
					setDashboardModules(defaultModules);
				}
			}
		};
		fetchDashboardModules();
		
		return () => {
			isMounted = false;
		};
	}, []);

	// Fetch debt concepts
	useEffect(() => {
		let isMounted = true;
		
		const fetchDebtConcepts = async () => {
			try {
				const { data } = await DebtConceptService.list({ active: true });
				if (isMounted) {
					// Extract concept names from the fetched concepts
					const conceptNames = data.map(concept => concept.name);
					setDebtConcepts(conceptNames);
				}
			} catch (err) {
				console.error('Error fetching debt concepts:', err);
				// If error, fall back to extracting from debts
				if (isMounted && debts && Array.isArray(debts)) {
					const conceptsSet = new Set();
					debts.forEach(debt => {
						if (debt && debt.concept) {
							conceptsSet.add(debt.concept);
						}
					});
					setDebtConcepts(Array.from(conceptsSet));
				}
			}
		};
		
		fetchDebtConcepts();
		
		return () => {
			isMounted = false;
		};
	}, [debts]);

	useEffect(() => {
		let isMounted = true;
		
		const fetchDashboardData = async () => {
			try {
				if (!isMounted) {
					return;
				}
				dispatch({ type: 'FETCH_REQUEST' });

				// Fechas del mes actual
				const currentMonthStart = moment.utc().startOf('month').format('YYYY-MM-DD');
				const currentMonthEnd = moment.utc().endOf('month').format('YYYY-MM-DD');
				
				// Fechas del mes pasado para comparar
				const previousMonthStart = moment.utc().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
				const previousMonthEnd = moment.utc().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');

				// Pagos de este mes (ingresos)
				const paymentsParams = new URLSearchParams();
				paymentsParams.set('dateFrom', currentMonthStart);
				paymentsParams.set('dateTo', currentMonthEnd);
				paymentsParams.set('allData', 'true');

				// Pagos del mes pasado para comparar
				const previousMonthPaymentsParams = new URLSearchParams();
				previousMonthPaymentsParams.set('dateFrom', previousMonthStart);
				previousMonthPaymentsParams.set('dateTo', previousMonthEnd);
				previousMonthPaymentsParams.set('allData', 'true');

				// Cuotas pendientes: del mes actual pero que todavía no vencieron
				const today = moment.utc().startOf('day').format('YYYY-MM-DD');
				const pendingParams = new URLSearchParams();
				pendingParams.set('expirationFrom', today);
				pendingParams.set('expirationTo', currentMonthEnd);
				pendingParams.set('paid', 'no');
				pendingParams.set('allData', 'true');

				// Cuotas vencidas: del mes actual que ya pasaron
				const overdueParams = new URLSearchParams();
				overdueParams.set('expirationFrom', currentMonthStart);
				overdueParams.set('expirationTo', moment.utc().startOf('day').subtract(1, 'day').format('YYYY-MM-DD'));
				overdueParams.set('paid', 'no');
				overdueParams.set('allData', 'true');

				// Próximos vencimientos: los próximos 14 días, cualquier mes
				const upcomingParams = new URLSearchParams();
				upcomingParams.set('expirationFrom', today);
				upcomingParams.set('expirationTo', moment.utc().add(14, 'days').format('YYYY-MM-DD'));
				upcomingParams.set('paid', 'no');
				upcomingParams.set('allData', 'true');

				// allSettled: one failing endpoint shouldn't blank the whole dashboard —
				// the page shell (calendar, quick actions) is always visible now, so a
				// single rejected request should only dash its own tiles/panels rather
				// than replace the entire page with an error message.
				const settled = await Promise.allSettled([
					AccountService.listForDashboard(),
					ContractService.listForDashboard(),
					AssetSaleService.listForDashboard(),
					AssetService.listForDashboard(),
					PaymentService.search(paymentsParams.toString()),
					GeneratedDebtService.search(pendingParams.toString()),
					GeneratedDebtService.search(overdueParams.toString()),
					DebtService.listForDashboard({ populate: true }),
					CreditService.listForDashboard({ populate: true }),
					PaymentService.search(previousMonthPaymentsParams.toString()),
					GeneratedDebtService.search(upcomingParams.toString()),
				]);

				if (!isMounted) {
					return;
				}

				const rejected = settled.filter((r) => r.status === 'rejected');
				rejected.forEach((r) => console.error('Error fetching a dashboard data segment:', r.reason));
				if (rejected.length === settled.length) {
					dispatch({ type: 'FETCH_FAIL', payload: getError(rejected[0].reason) });
					return;
				}

				const [
					accountsData,
					contractsData,
					assetSalesData,
					assetsData,
					paymentsData,
					pendingDuesData,
					overdueDuesData,
					debtsData,
					creditsData,
					previousMonthPaymentsData,
					upcomingDuesData,
				] = settled.map((r) => (r.status === 'fulfilled' ? r.value : { data: [] }));

				dispatch({
					type: 'FETCH_SUCCESS',
					payload: {
						accounts: accountsData.data || [],
						contracts: contractsData.data || [],
						assetSales: assetSalesData.data || [],
						assets: assetsData.data || [],
						payments: paymentsData.data?.allPayments || paymentsData.data?.payments || paymentsData.data || [],
						generatedDebts: {
							pending: pendingDuesData.data?.allDebtsDues || pendingDuesData.data?.debtsDues || pendingDuesData.data || [],
							overdue: overdueDuesData.data?.allDebtsDues || overdueDuesData.data?.debtsDues || overdueDuesData.data || [],
						},
						debts: debtsData.data || [],
						credits: creditsData.data || [],
						previousMonthPayments: previousMonthPaymentsData.data?.allPayments || previousMonthPaymentsData.data?.payments || previousMonthPaymentsData.data || [],
						upcomingDues: upcomingDuesData.data?.allDebtsDues || upcomingDuesData.data?.debtsDues || upcomingDuesData.data || [],
					},
				});
			} catch (err) {
				console.error('Error fetching dashboard data:', err);
				if (!isMounted) {
					return;
				}
				dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
			}
		};

		fetchDashboardData();

		return () => {
			isMounted = false;
		};
	}, []);

	// Fechas del mes actual para contar cosas nuevas
	const currentMonthStart = moment().startOf('month').startOf('day');
	const currentMonthEnd = moment().endOf('month').endOf('day');

	// Cuántas cuentas nuevas hay este mes
	const newAccountsCurrentMonth = accounts.filter(account => {
		if (!account.createdAt) return false;
		const createdAt = moment(account.createdAt).startOf('day');
		return createdAt.isSameOrAfter(currentMonthStart) && createdAt.isSameOrBefore(currentMonthEnd);
	}).length;

	// Cuántos contratos nuevos hay este mes
	const newContractsCurrentMonth = contracts.filter(contract => {
		if (!contract.createdAt) return false;
		const createdAt = moment(contract.createdAt).startOf('day');
		return createdAt.isSameOrAfter(currentMonthStart) && createdAt.isSameOrBefore(currentMonthEnd);
	}).length;

	// Cuántas ventas nuevas hay este mes
	const newAssetSalesCurrentMonth = assetSales.filter(sale => {
		if (!sale.createdAt) return false;
		const createdAt = moment(sale.createdAt).startOf('day');
		return createdAt.isSameOrAfter(currentMonthStart) && createdAt.isSameOrBefore(currentMonthEnd);
	}).length;

	// Cuántos activos nuevos hay este mes
	const newAssetsCurrentMonth = assets.filter(asset => {
		if (!asset.createdAt) return false;
		const createdAt = moment(asset.createdAt).startOf('day');
		return createdAt.isSameOrAfter(currentMonthStart) && createdAt.isSameOrBefore(currentMonthEnd);
	}).length;

	// Get all debt concepts from API (already fetched in useEffect)
	const getAllConcepts = () => {
		// Use the debt concepts fetched from the API
		// If concepts haven't loaded yet, fall back to extracting from debts
		if (debtConcepts && debtConcepts.length > 0) {
			return debtConcepts;
		}
		
		// Fallback: extract from debts if API hasn't loaded yet
		const conceptsSet = new Set();
		if (debts && Array.isArray(debts)) {
			debts.forEach(debt => {
				if (debt && debt.concept) {
					conceptsSet.add(debt.concept);
				}
			});
		}
		return Array.from(conceptsSet);
	};

	// Contar cuánto se pagó por cada concepto este mes, separado por dólares y pesos
	const calculatePaymentsByConcept = () => {
		const concepts = getAllConcepts();
		const conceptMap = {};

		concepts.forEach(concept => {
			conceptMap[concept] = {
				name: concept,
				paid: { 
					count: 0, 
					amountUSD: 0, 
					amountUYU: 0 
				},
			};
		});

		if (payments && Array.isArray(payments)) {
			payments.forEach(payment => {
				if (payment && payment.debt && payment.debt.concept && payment.amount) {
					const concept = payment.debt.concept;
					const currency = payment.debt.currency || payment.currency;
					if (conceptMap[concept]) {
						conceptMap[concept].paid.count++;
						if (currency === 'USD') {
							conceptMap[concept].paid.amountUSD += payment.amount || 0;
						} else if (currency === 'UYU') {
							conceptMap[concept].paid.amountUYU += payment.amount || 0;
						}
					}
				}
			});
		}

		return conceptMap;
	};

	// Crear un mapa rápido para buscar deudas por ID (igual que en el calendario)
	const debtsMap = {};
	if (debts && Array.isArray(debts)) {
		debts.forEach(debt => {
			if (debt && debt._id) {
				debtsMap[debt._id] = debt;
			}
		});
	}

	// Contar cuotas pendientes y vencidas por concepto, separado por dólares y pesos
	// Si tiene contract, es de alquiler (igual que en el calendario)
	const calculateDuesByConcept = () => {
		const concepts = getAllConcepts();
		const conceptMap = {};

		concepts.forEach(concept => {
			conceptMap[concept] = {
				name: concept,
				pending: { count: 0, amountUSD: 0, amountUYU: 0 },
				overdue: { count: 0, amountUSD: 0, amountUYU: 0 },
			};
		});

		// Cubeta sintética para cuotas ligadas a un contrato (no se persiste;
		// la consume la tarjeta de contratos y los totales de más abajo)
		conceptMap['contractLinked'] = {
			name: 'contractLinked',
			pending: { count: 0, amountUSD: 0, amountUYU: 0 },
			overdue: { count: 0, amountUSD: 0, amountUYU: 0 },
		};

		if (generatedDebts?.pending && Array.isArray(generatedDebts.pending)) {
			generatedDebts.pending.forEach(due => {
				if (due && due.debt) {
					// Buscar la deuda completa (igual que en el calendario)
					const debt = due.debt;
					const debtId = debt?._id || debt?.toString();
					const fullDebt = debtsMap[debtId] || debt;
					
					// Ver si tiene contract (igual que en el calendario)
					const contractRef = fullDebt?.contract || (debt && typeof debt === 'object' && debt.contract);
					const hasContract = !!contractRef;
					
					// Si tiene contract, va a la cubeta de contratos; si no, usar el concepto
					const concept = hasContract ? 'contractLinked' : (fullDebt?.concept || debt?.concept);

					if (concept && conceptMap[concept]) {
						const currency = fullDebt?.currency || debt?.currency;
						conceptMap[concept].pending.count++;
						// Use unpaidAmount virtual field if available
						let unpaidAmount = 0;
						if (due.unpaidAmount !== undefined && due.unpaidAmount !== null) {
							unpaidAmount = due.unpaidAmount;
						} else if (due.amount !== undefined) {
							const totalPaid = due.payments && Array.isArray(due.payments) 
								? due.payments.reduce((sum, p) => sum + (p.amount || 0), 0) 
								: 0;
							unpaidAmount = Math.max(0, (due.amount || 0) - totalPaid);
						}
						if (currency === 'USD') {
							conceptMap[concept].pending.amountUSD += unpaidAmount;
						} else if (currency === 'UYU') {
							conceptMap[concept].pending.amountUYU += unpaidAmount;
						}
					}
				}
			});
		}

		if (generatedDebts?.overdue && Array.isArray(generatedDebts.overdue)) {
			generatedDebts.overdue.forEach(due => {
				if (due && due.debt) {
					// Buscar la deuda completa (igual que en el calendario)
					const debt = due.debt;
					const debtId = debt?._id || debt?.toString();
					const fullDebt = debtsMap[debtId] || debt;
					
					// Ver si tiene contract (igual que en el calendario)
					const contractRef = fullDebt?.contract || (debt && typeof debt === 'object' && debt.contract);
					const hasContract = !!contractRef;
					
					// Si tiene contract, va a la cubeta de contratos; si no, usar el concepto
					const concept = hasContract ? 'contractLinked' : (fullDebt?.concept || debt?.concept);

					if (concept && conceptMap[concept]) {
						const currency = fullDebt?.currency || debt?.currency;
						conceptMap[concept].overdue.count++;
						let unpaidAmount = 0;
						if (due.unpaidAmount !== undefined && due.unpaidAmount !== null) {
							unpaidAmount = due.unpaidAmount;
						} else if (due.amount !== undefined) {
							const totalPaid = due.payments && Array.isArray(due.payments) 
								? due.payments.reduce((sum, p) => sum + (p.amount || 0), 0) 
								: 0;
							unpaidAmount = Math.max(0, (due.amount || 0) - totalPaid);
						}
						if (currency === 'USD') {
							conceptMap[concept].overdue.amountUSD += unpaidAmount;
						} else if (currency === 'UYU') {
							conceptMap[concept].overdue.amountUYU += unpaidAmount;
						}
					}
				}
			});
		}

		return conceptMap;
	};

	const paymentsByConcept = calculatePaymentsByConcept();
	const duesByConcept = calculateDuesByConcept();
	const allConcepts = getAllConcepts();

	// Sumar todo lo que se pagó, está pendiente y venció, por concepto y moneda
	const totalPaid = {
		count: 0,
		amountUSD: 0,
		amountUYU: 0,
	};

	const totalPending = {
		count: 0,
		amountUSD: 0,
		amountUYU: 0,
	};

	const totalOverdue = {
		count: 0,
		amountUSD: 0,
		amountUYU: 0,
	};

	allConcepts.forEach(concept => {
		if (paymentsByConcept[concept]) {
			totalPaid.count += paymentsByConcept[concept].paid.count;
			totalPaid.amountUSD += paymentsByConcept[concept].paid.amountUSD;
			totalPaid.amountUYU += paymentsByConcept[concept].paid.amountUYU;
		}
		if (duesByConcept[concept]) {
			totalPending.count += duesByConcept[concept].pending.count;
			totalPending.amountUSD += duesByConcept[concept].pending.amountUSD;
			totalPending.amountUYU += duesByConcept[concept].pending.amountUYU;
			totalOverdue.count += duesByConcept[concept].overdue.count;
			totalOverdue.amountUSD += duesByConcept[concept].overdue.amountUSD;
			totalOverdue.amountUYU += duesByConcept[concept].overdue.amountUYU;
		}
	});

	// La cubeta sintética de contratos no forma parte de allConcepts:
	// sumarla aparte para que las cuotas ligadas a contratos entren en los totales
	if (duesByConcept['contractLinked'] && !allConcepts.includes('contractLinked')) {
		totalPending.count += duesByConcept['contractLinked'].pending.count;
		totalPending.amountUSD += duesByConcept['contractLinked'].pending.amountUSD;
		totalPending.amountUYU += duesByConcept['contractLinked'].pending.amountUYU;
		totalOverdue.count += duesByConcept['contractLinked'].overdue.count;
		totalOverdue.amountUSD += duesByConcept['contractLinked'].overdue.amountUSD;
		totalOverdue.amountUYU += duesByConcept['contractLinked'].overdue.amountUYU;
	}

	// Sumar solo las cuotas ligadas a contratos (para la tarjeta de contratos)
	const rentalPending = duesByConcept['contractLinked']?.pending || { count: 0, amountUSD: 0, amountUYU: 0 };
	const rentalOverdue = duesByConcept['contractLinked']?.overdue || { count: 0, amountUSD: 0, amountUYU: 0 };

	// Format currency
	const formatCurrency = (amount, currency = null) => {
		const formattedAmount = new Intl.NumberFormat('es-UY', {
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
		
		if (currency) {
			const currencySymbol = currency === 'USD' ? 'U$S' : currency === 'UYU' ? '$U' : '';
			return currencySymbol ? `${currencySymbol} ${formattedAmount}` : formattedAmount;
		}
		return formattedAmount;
	};

	// Metricas Cards
	// Metricas Cuentas
	const accountsWithDebts = accounts.filter(account => account.debts && account.debts.length > 0).length;
	const accountsWithCredits = accounts.filter(account => account.credits && account.credits.length > 0).length;
	
	// Metricas Contratos
	const activeContracts = contracts.filter(contract => !contract.closeDate || moment(contract.closeDate).isAfter(moment())).length;
	const closedContracts = contracts.filter(contract => contract.closeDate && moment(contract.closeDate).isBefore(moment())).length;
	
	// Metricas Ventass, separadas por moneda
	const totalSalesUSD = assetSales.filter(sale => sale.currency === 'USD').reduce((sum, sale) => sum + (sale.amount || 0), 0);
	const totalSalesUYU = assetSales.filter(sale => sale.currency === 'UYU').reduce((sum, sale) => sum + (sale.amount || 0), 0);
	const currentMonthSalesUSD = assetSales.filter(sale => {
		if (!sale.createdAt || sale.currency !== 'USD') return false;
		const createdAt = moment(sale.createdAt).startOf('day');
		return createdAt.isSameOrAfter(currentMonthStart) && createdAt.isSameOrBefore(currentMonthEnd);
	}).reduce((sum, sale) => sum + (sale.amount || 0), 0);
	const currentMonthSalesUYU = assetSales.filter(sale => {
		if (!sale.createdAt || sale.currency !== 'UYU') return false;
		const createdAt = moment(sale.createdAt).startOf('day');
		return createdAt.isSameOrAfter(currentMonthStart) && createdAt.isSameOrBefore(currentMonthEnd);
	}).reduce((sum, sale) => sum + (sale.amount || 0), 0);
	
	// Metricas de activos
	const soldAssets = assets.filter(asset => asset.sold === true).length;
	const availableAssets = assets.filter(asset => asset.sold === false || !asset.sold).length;
	
	// Calculos de ingresos (payments) por moneda 
	const paymentsUSD = payments.filter(p => p.currency === 'USD').reduce((sum, p) => sum + (p.amount || 0), 0);
	const paymentsUYU = payments.filter(p => p.currency === 'UYU').reduce((sum, p) => sum + (p.amount || 0), 0);
	
	// Calculos de ingresos (payments) mes anterior por moneda
	const previousMonthPaymentsUSD = previousMonthPayments.filter(p => p.currency === 'USD').reduce((sum, p) => sum + (p.amount || 0), 0);
	const previousMonthPaymentsUYU = previousMonthPayments.filter(p => p.currency === 'UYU').reduce((sum, p) => sum + (p.amount || 0), 0);
	
	// Comparación con mes anterior por moneda
	// Ver cuánto cambió el porcentaje
	const incomeDifferenceUSD = paymentsUSD - previousMonthPaymentsUSD;
	const incomeChangePercentUSD = previousMonthPaymentsUSD > 0 
		? ((incomeDifferenceUSD / previousMonthPaymentsUSD) * 100).toFixed(1)
		: paymentsUSD > 0 ? '100' : '0';
	
	const incomeDifferenceUYU = paymentsUYU - previousMonthPaymentsUYU;
	const incomeChangePercentUYU = previousMonthPaymentsUYU > 0
		? ((incomeDifferenceUYU / previousMonthPaymentsUYU) * 100).toFixed(1)
		: paymentsUYU > 0 ? '100' : '0';

	// Saludo + subtítulo de la cabecera, derivados de los mismos datos que
	// alimentan las tiles/tabla de abajo para que nunca queden desincronizados.
	const userInfo = getUserInfo();
	const firstName = userInfo?.name ? userInfo.name.split(' ')[0] : '';
	const hour = moment().hour();
	const greeting = hour < 12 ? 'Buen día' : hour < 19 ? 'Buenas tardes' : 'Buenas noches';
	const weekEnd = moment().endOf('week');
	const pendingThisWeek = (generatedDebts.pending || []).filter(due => moment(due.expiration).isSameOrBefore(weekEnd, 'day')).length;
	const dueThisWeekCount = pendingThisWeek + (generatedDebts.overdue || []).length;
	const todayLabel = moment().format('dddd D [de] MMMM');
	// Dash rather than 0 while the initial fetch is in flight — otherwise the
	// header briefly claims "0 Cuentas activas" before the real count lands.
	const dash = (value) => (loaded ? value : '—');
	const subtitle = `${todayLabel.charAt(0).toUpperCase()}${todayLabel.slice(1)} · ${dash(accounts.length)} ${t('entity.account.plural')} activas · ${dash(dueThisWeekCount)} vencimientos esta semana`;

	return (
		<EntityList>
			<Helmet>
				<title>Dashboard - Inicio</title>
			</Helmet>

			<PageHead
				title={firstName ? `${greeting}, ${firstName}` : greeting}
				subtitle={subtitle}
			/>

			{error && <MessageBox variant='danger'>{error}</MessageBox>}

			<QuickActionsRow />

			{dashboardModules.includes('calendar') && (
				<Row className='mb-4'>
					<Col>
						<CalendarView dashboardModules={dashboardModules} />
					</Col>
				</Row>
			)}

			<KpiGrid
				dashboardModules={dashboardModules}
				loaded={loaded}
				accounts={accounts}
				newAccountsCurrentMonth={newAccountsCurrentMonth}
				contracts={contracts}
				newContractsCurrentMonth={newContractsCurrentMonth}
				activeContracts={activeContracts}
				closedContracts={closedContracts}
				rentalPending={rentalPending}
				rentalOverdue={rentalOverdue}
				assetSales={assetSales}
				newAssetSalesCurrentMonth={newAssetSalesCurrentMonth}
				currentMonthSalesUSD={currentMonthSalesUSD}
				currentMonthSalesUYU={currentMonthSalesUYU}
				assets={assets}
				newAssetsCurrentMonth={newAssetsCurrentMonth}
				totalSalesUSD={totalSalesUSD}
				totalSalesUYU={totalSalesUYU}
				soldAssets={soldAssets}
				availableAssets={availableAssets}
				credits={credits}
				debts={debts}
				accountsWithDebts={accountsWithDebts}
				accountsWithCredits={accountsWithCredits}
				formatCurrency={formatCurrency}
			/>

			{/* Ingresos + Pagos panel */}
			{isFacturacionEnabled() && dashboardModules.includes('facturacion') && (
				<IncomePaymentsPanel
					loading={loading}
					loaded={loaded}
					paymentsUSD={paymentsUSD}
					paymentsUYU={paymentsUYU}
					previousMonthPaymentsUSD={previousMonthPaymentsUSD}
					previousMonthPaymentsUYU={previousMonthPaymentsUYU}
					incomeChangePercentUSD={incomeChangePercentUSD}
					incomeChangePercentUYU={incomeChangePercentUYU}
					allConcepts={allConcepts}
					paymentsByConcept={paymentsByConcept}
					duesByConcept={duesByConcept}
					totalPaid={totalPaid}
					totalPending={totalPending}
					totalOverdue={totalOverdue}
					formatCurrency={formatCurrency}
				/>
			)}

			{/* Próximos vencimientos / Actividad reciente */}
			<div className='tw:grid tw:grid-cols-[1.5fr_1fr] tw:max-lg:grid-cols-1 tw:gap-4 tw:mb-6'>
				<UpcomingDues dues={upcomingDues} formatCurrency={formatCurrency} loading={loading} />
				<ActivityFeed />
			</div>
		</EntityList>
	);
}

export default Dashboard;
